import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { api } from "@/api/client";
import {
  resolveServiceLabel,
  useLabels,
  useSetServiceLabel,
} from "@/api/local/labels";
import type { Pool, Service } from "@/api/types";
import { useAlert } from "@/components/ConfirmDialog";
import { SplitPane } from "@/components/SplitPane";
import { GlobalDetail } from "@/pages/workloads/GlobalDetail";
import { PoolDetail } from "@/pages/workloads/PoolDetail";
import { PodDetail } from "@/pages/workloads/PodDetail";
import { ServiceDetail } from "@/pages/workloads/ServiceDetail";
import { useAllPools, useAllReplicas, useServices } from "@/workloads/data";
import {
  GLOBAL_FOCUS,
  type Focus,
  parseFocus,
  serializeFocus,
} from "@/workloads/focus";
import { defaultTab, isValidTab } from "@/workloads/scope";
import { Tree } from "@/workloads/Tree";
import {
  GLOBAL_TREE_KEY,
  ancestorKeys,
  buildTree,
  type TreeNode,
} from "@/workloads/tree-data";

export function Workloads() {
  const [params, setParams] = useSearchParams();
  const focus = parseFocus(params.get("focus"));
  const tab = params.get("tab") ?? defaultTab(focus.type);

  const services = useServices();
  const replicas = useAllReplicas();
  const serviceIds = services.data?.map((s) => s.id);
  const pools = useAllPools(serviceIds);
  const labels = useLabels();
  const setServiceLabel = useSetServiceLabel();
  const queryClient = useQueryClient();
  const alert = useAlert();

  // Inline service creation: instead of the multi-step wizard, drop a
  // draft row into the tree immediately and jump to its Workload tab.
  // The controller forces every fresh service into
  // `lifecycle_state=draft` (and `enabled=false`) so it doesn't get
  // picked up by routing / provisioning until the first rollout
  // completes.
  //
  // The slug (`Service.name`) is a short random hash because the
  // controller treats it as immutable; we want collision-free, terse,
  // and recognisable-as-not-a-real-name. The user-facing label is
  // editable from the bottom pane's left menu (SubNav title).
  const createService = useMutation({
    mutationFn: async () => {
      const slug = `svc-${randomHash(8)}`;
      const svc = await api.post<Service>("/services", {
        name: slug,
        description: null,
        default_threshold_ms: 2000,
      });
      return svc;
    },
    onSuccess: async (svc) => {
      await setServiceLabel(svc.id, "New service").catch(() => undefined);
      queryClient.invalidateQueries({ queryKey: ["services"] });
      const next = new URLSearchParams();
      const focusValue = serializeFocus({ type: "service", id: svc.id });
      if (focusValue) next.set("focus", focusValue);
      next.set("tab", "workload");
      setParams(next);
    },
    onError: (err) => {
      void alert({
        title: "Failed to create service",
        tone: "danger",
        description:
          err instanceof Error ? err.message : "Failed to create service.",
      });
    },
  });

  const onAddService = useCallback(() => {
    createService.mutate();
  }, [createService]);

  // The Global root is open by default so the service list is always
  // visible on first load. Users can collapse it like any other row.
  const [expanded, setExpanded] = useState<Set<string>>(
    () => new Set([GLOBAL_TREE_KEY]),
  );

  // Pool creation lives on the tree itself: every service row carries
  // an inline "+ pool" button (`Tree`'s `onAddPool` callback). Mirroring
  // the "+ service" pattern, clicking it POSTs a placeholder pool with
  // sensible-but-inert defaults (max_replicas=0 so nothing provisions),
  // appends it to the tree, and jumps focus to its Settings tab so the
  // operator can tune resource shape / placements / capacity in-place.
  // We track the in-flight target service id so the row's button can
  // show a spinner without locking out unrelated services.
  const [addingPoolForServiceId, setAddingPoolForServiceId] = useState<
    number | null
  >(null);

  const createPool = useMutation({
    mutationFn: async (service: Service) => {
      const existing = (pools.data ?? []).filter(
        (p) => p.service_id === service.id,
      );
      const payload = {
        priority: nextPriority(existing),
        // Operator-editable label. We seed with a short random string
        // so the tree row has something terse to render before the
        // operator picks a real shape; the title stays the same even
        // when they later edit the shape (it's no longer a function
        // of accelerators/cpus). The pencil-edit flow PATCHes the
        // same column.
        display_name: `pool-${randomHash(5)}`,
        // Draft pool: no shape committed. The operator picks GPU vs
        // CPU on the Pool Spec tab; the controller refuses to bake a
        // hardcoded "H100:1" into ``sky_service_name`` for a pool the
        // operator never explicitly configured (the slug gets
        // rewritten on the first real PATCH — see
        // ``controller/api/routers/pools.py::_maybe_promote_slug``).
        // Cloud / region candidates are derived from the Provider
        // config inheritance chain at render time, not stored on the
        // pool, so there's no placement field to seed here.
        accelerators: null,
        cpus: null,
        memory_gb: null,
        disk_gb: null,
        num_nodes: 1,
        // Inert by default. The reconciler / planner won't try to
        // spawn anything until the operator dials this up from the
        // Spec tab — gives them a chance to pick the real shape and
        // adjust providers before any cloud bill starts.
        max_replicas: 0,
        max_price_per_hour: 3.5,
      };
      return api.post<Pool>(`/services/${service.id}/pools`, payload);
    },
    onMutate: (service) => {
      setAddingPoolForServiceId(service.id);
    },
    onSettled: () => {
      setAddingPoolForServiceId(null);
    },
    onSuccess: (pool) => {
      queryClient.invalidateQueries({ queryKey: ["pools"] });
      const next = new URLSearchParams();
      const focusValue = serializeFocus({ type: "pool", id: pool.id });
      if (focusValue) next.set("focus", focusValue);
      next.set("tab", "settings");
      setParams(next);
    },
    onError: (err) => {
      void alert({
        title: "Failed to create pool",
        tone: "danger",
        description:
          err instanceof Error ? err.message : "Failed to create pool.",
      });
    },
  });

  const tree = useMemo(() => {
    const list = services.data ?? [];
    const byService = new Map<number, Pool[]>();
    for (const p of pools.data ?? []) {
      const arr = byService.get(p.service_id) ?? [];
      arr.push(p);
      byService.set(p.service_id, arr);
    }
    return buildTree(list, byService, replicas.data ?? [], {
      service: (svc) => resolveServiceLabel(labels, svc.id, svc.name),
    });
  }, [services.data, pools.data, replicas.data, labels]);

  // Auto-expand ancestors of the current focus so the row is visible.
  // We key the effect off the serialized focus (a primitive string)
  // rather than the parsed object — `parseFocus` returns a fresh object
  // every render, and depending on it directly would make this effect
  // re-run on every render and constantly re-open ancestors the user
  // just collapsed via the chevron.
  const focusKey = serializeFocus(focus) ?? GLOBAL_TREE_KEY;
  useEffect(() => {
    if (focus.type === "global" || focus.id === null) return;
    const targetKey = `${focus.type}:${focus.id}`;
    setExpanded((prev) => {
      const next = new Set(prev);
      let mutated = false;
      for (const key of ancestorKeys(tree, targetKey)) {
        if (!next.has(key)) {
          next.add(key);
          mutated = true;
        }
      }
      return mutated ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusKey, tree]);

  const setFocusAndTab = useCallback(
    (nextFocus: Focus, nextTab: string) => {
      const sp = new URLSearchParams();
      const focusValue = serializeFocus(nextFocus);
      if (focusValue) sp.set("focus", focusValue);
      const allowed = isValidTab(nextFocus.type, nextTab)
        ? nextTab
        : defaultTab(nextFocus.type);
      sp.set("tab", allowed);
      setParams(sp, { replace: false });
    },
    [setParams],
  );

  const onToggle = useCallback((key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const onAddPool = useCallback(
    (service: Service) => {
      createPool.mutate(service);
    },
    [createPool],
  );

  const onSelect = useCallback(
    (node: TreeNode) => {
      // We carry the *current* tab forward when switching rows; the
      // setter validates it against the new scope's tab list and falls
      // back to that scope's default if it doesn't apply (e.g. moving
      // from Service > Workload to a Pool, which has no Workload tab).
      // This way operators stay on Cost / Settings / etc. while
      // browsing the tree instead of getting bounced to Dashboard
      // every time they pick a different row.
      // Auto-expand so the focused row's children become visible, then
      // move focus.
      if (node.children.length > 0) {
        setExpanded((prev) => {
          if (prev.has(node.key)) return prev;
          const next = new Set(prev);
          next.add(node.key);
          return next;
        });
      }
      if (node.type === "global") {
        setFocusAndTab(GLOBAL_FOCUS, tab);
        return;
      }
      if (node.id === null) return;
      setFocusAndTab(
        { type: node.type as Focus["type"], id: node.id },
        tab,
      );
    },
    [setFocusAndTab, tab],
  );

  // Resolve the entities the bottom pane needs based on the current focus.
  const detail = useMemo(() => {
    if (focus.type === "global" || focus.id === null) {
      return { kind: "global" as const };
    }
    if (focus.type === "service") {
      const svc = services.data?.find((s) => s.id === focus.id);
      if (!svc)
        return { kind: "missing" as const, label: `service #${focus.id}` };
      return { kind: "service" as const, service: svc };
    }
    if (focus.type === "pool") {
      const pool = pools.data?.find((p) => p.id === focus.id);
      if (!pool) return { kind: "missing" as const, label: `pool #${focus.id}` };
      const svc = services.data?.find((s) => s.id === pool.service_id);
      if (!svc)
        return { kind: "missing" as const, label: `service of pool #${focus.id}` };
      return { kind: "pool" as const, pool, service: svc };
    }
    // pod
    const podRecord = (replicas.data ?? []).flatMap((r) =>
      r.pods.map((p) => ({ pod: p, replica: r })),
    );
    const found = podRecord.find((x) => x.pod.id === focus.id);
    if (found) {
      const pool = pools.data?.find((p) => p.id === found.replica.pool_id);
      if (!pool)
        return { kind: "missing" as const, label: `pool of pod #${focus.id}` };
      const svc = services.data?.find((s) => s.id === pool.service_id);
      if (!svc)
        return { kind: "missing" as const, label: `service of pod #${focus.id}` };
      return {
        kind: "pod" as const,
        pod: found.pod,
        replica: found.replica,
        pool,
        service: svc,
      };
    }
    // Replica fallback for replicas without recorded pods.
    const replicaFallback = (replicas.data ?? []).find((r) => r.id === focus.id);
    if (replicaFallback) {
      const pool = pools.data?.find((p) => p.id === replicaFallback.pool_id);
      const svc = pool
        ? services.data?.find((s) => s.id === pool.service_id)
        : undefined;
      if (pool && svc) {
        return {
          kind: "pod" as const,
          pod: null,
          replica: replicaFallback,
          pool,
          service: svc,
        };
      }
    }
    return { kind: "missing" as const, label: `pod #${focus.id}` };
  }, [focus, services.data, pools.data, replicas.data]);

  return (
    <div className="workloads">
      <SplitPane storageKey="workloads.split.v3" defaultRatio={0.32}>
        <div className="workloads__tree">
          <Tree
            roots={tree}
            focus={focus}
            expanded={expanded}
            onToggle={onToggle}
            onSelect={onSelect}
            onAddPool={onAddPool}
            addPoolPendingForServiceId={addingPoolForServiceId}
            onAddService={onAddService}
            addServicePending={createService.isPending}
            loading={services.isLoading || replicas.isLoading}
          />
        </div>
        <div className="workloads__detail">
          {detail.kind === "global" && (
            <GlobalDetail tab={tab} setFocusAndTab={setFocusAndTab} />
          )}
          {detail.kind === "service" && (
            <ServiceDetail
              service={detail.service}
              pools={pools.data ?? []}
              replicas={replicas.data ?? []}
              tab={tab}
              setFocusAndTab={setFocusAndTab}
            />
          )}
          {detail.kind === "pool" && (
            <PoolDetail
              pool={detail.pool}
              service={detail.service}
              replicas={replicas.data ?? []}
              tab={tab}
              setFocusAndTab={setFocusAndTab}
            />
          )}
          {detail.kind === "pod" && (
            <PodDetail
              pod={detail.pod}
              replica={detail.replica}
              pool={detail.pool}
              service={detail.service}
              tab={tab}
              setFocusAndTab={setFocusAndTab}
            />
          )}
          {detail.kind === "missing" && (
            <div className="empty-state" style={{ margin: 24 }}>
              <div className="empty-state__title">{detail.label} not found</div>
              <div>
                It may have been deleted.{" "}
                <button
                  className="btn btn--ghost btn--sm"
                  onClick={() => setFocusAndTab(GLOBAL_FOCUS, "dashboard")}
                >
                  back to global
                </button>
              </div>
            </div>
          )}
        </div>
      </SplitPane>
    </div>
  );
}

/**
 * Lowest unused priority within an existing pool list. Mirrors the
 * value the deleted "Add a pool" modal used to seed the priority
 * field with — keeps tree ordering predictable as the operator stamps
 * out fresh placeholder pools.
 */
function nextPriority(pools: Pool[]): number {
  if (pools.length === 0) return 0;
  return Math.max(...pools.map((p) => p.priority)) + 1;
}

/**
 * Short random hex string used as the immutable slug for a fresh
 * service row. Uses `crypto.randomUUID` when available (every modern
 * browser) and falls back to Math.random for dev contexts that lack
 * the WebCrypto API. 8 hex chars (~4.3B combinations) is enough that
 * two operators clicking "+ service" within the same millisecond
 * don't collide.
 */
function randomHash(len: number): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID().replace(/-/g, "").slice(0, len);
  }
  let out = "";
  while (out.length < len) {
    out += Math.random().toString(16).slice(2);
  }
  return out.slice(0, len);
}
